import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/service-models", label: "Service Models" },
  { to: "/ai-delivery", label: "AI in Delivery" },
  { to: "/engage", label: "Engage" },
  { to: "/join-network", label: "Delivery Network" },
  { to: "/contact", label: "Contact" },
  { to: "/privacy", label: "Privacy" },
];

export default function PublicNav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      data-testid="public-nav"
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-white/85 border-b border-border"
          : "bg-white/60 backdrop-blur-md border-b border-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-12 h-16 flex items-center justify-between gap-6">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5 group shrink-0">
          <span className="brand-dot" data-testid="nav-brand-dot" aria-hidden="true" />
          <span className="font-heading text-xl font-bold tracking-tight">Nexra</span>
          <span className="hidden sm:inline-flex items-center gap-1.5 ml-2 pl-3 border-l border-border text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground">
            <span className="status-dot" />
            AI-Enabled
          </span>
        </Link>

        <nav className="hidden xl:flex items-center gap-0">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `px-3 py-2 text-sm font-medium transition-colors duration-200 ${
                  isActive ? "text-obsidian" : "text-muted-foreground hover:text-obsidian"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden xl:flex items-center gap-3 shrink-0">
          <Link to="/engage" data-testid="nav-cta-engage" className="btn-primary text-xs">
            Engage Nexra
          </Link>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className="xl:hidden p-3 border border-border min-w-[44px] min-h-[44px] inline-flex items-center justify-center"
          aria-label="Toggle menu"
          aria-expanded={open}
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div className="xl:hidden border-t border-border bg-white max-h-[calc(100vh-4rem)] overflow-y-auto" data-testid="mobile-nav">
          <div className="px-6 py-4 flex flex-col gap-1">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `px-3 py-4 text-base font-medium border-b border-border ${
                    isActive ? "text-execution" : "text-foreground"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link to="/engage" data-testid="mobile-nav-cta" className="btn-primary mt-4 w-full">
              Engage Nexra
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
